import type { Product } from "@/mocks/products";
import ProductCard from "./ProductCard";
import Pagination from "./Pagination";
import SortDropdown from "./SortDropdown";

interface ProductListingProps {
  title: string;
  totalCount: number;
  products: Product[];
  sortId: string;
  onSortChange: (id: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onAddToCart: (product: Product) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

export default function ProductListing({
  title,
  totalCount,
  products,
  sortId,
  onSortChange,
  currentPage,
  totalPages,
  onPageChange,
  onAddToCart,
  hasActiveFilters,
  onClearFilters,
}: ProductListingProps) {
  return (
    <div className="product-listing min-w-0 flex-1">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-heading text-3xl font-semibold text-foreground-950">{title}</h2>
          <p className="mt-1 text-sm text-foreground-500">
            {totalCount} produtos encontrados
          </p>
        </div>
        <SortDropdown value={sortId} onChange={onSortChange} />
      </div>

      {hasActiveFilters && (
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={onClearFilters}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
          >
            <i className="ri-close-line text-sm"></i>
            Limpar filtros
          </button>
        </div>
      )}

      {products.length === 0 ? (
        <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 py-20 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-primary-500">
            <i className="ri-search-eye-line text-2xl"></i>
          </span>
          <p className="mt-4 text-sm text-foreground-600">
            Nenhum produto encontrado com esses filtros.
          </p>
          <button
            type="button"
            onClick={onClearFilters}
            className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-background-50 hover:bg-primary-600"
          >
            Limpar filtros
          </button>
        </div>
      ) : (
        <div className="product-grid mt-7 grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} onAddToCart={onAddToCart} />
          ))}
        </div>
      )}

      {totalPages >= 1 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
      )}
    </div>
  );
}
